import React from 'react';
import './ScheduleDropdown.less';

export type ScheduleDropdownOption = {
  label: string;
  value: string;
  disabled?: boolean;
  hint?: string;
};

type Props = {
  value: string;
  options: ScheduleDropdownOption[];
  placeholder?: string;
  disabled?: boolean;
  searchable?: boolean;
  ariaLabel?: string;
  className?: string;
  variant?: 'floating' | 'inline';
  onChange: (value: string) => void;
};

export default function ScheduleDropdown({
  value,
  options,
  placeholder = 'Selecionar',
  disabled = false,
  searchable = false,
  ariaLabel = 'Lista de opções',
  className = '',
  onChange,
  variant = 'floating',
}: Props) {
  const rootRef = React.useRef<HTMLDivElement | null>(null);
  const buttonRef = React.useRef<HTMLButtonElement | null>(null);
  const searchRef = React.useRef<HTMLInputElement | null>(null);
  const menuId = React.useId();

  const [isOpen, setIsOpen] = React.useState(false);
  const [isVisible, setIsVisible] = React.useState(false);
  const [query, setQuery] = React.useState('');
  const [highlightedIndex, setHighlightedIndex] = React.useState(0);

  const selectedOption = React.useMemo(
    () => options.find((option) => option.value === value) ?? null,
    [options, value],
  );

  const filteredOptions = React.useMemo(() => {
    const normalizedQuery = query.trim().toLowerCase();

    if (!searchable || !normalizedQuery) return options;

    return options.filter((option) =>
      option.label.toLowerCase().includes(normalizedQuery),
    );
  }, [options, query, searchable]);

  const closeDropdown = React.useCallback((focusTrigger = false) => {
    setIsOpen(false);
    setQuery('');
    setHighlightedIndex(0);

    window.setTimeout(() => {
      setIsVisible(false);

      if (focusTrigger) {
        buttonRef.current?.focus();
      }
    }, 180);
  }, []);

  const openDropdown = React.useCallback(() => {
    if (disabled) return;

    setIsVisible(true);

    requestAnimationFrame(() => {
      setIsOpen(true);

      if (searchable) {
        window.setTimeout(() => {
          searchRef.current?.focus();
        }, 10);
      }
    });
  }, [disabled, searchable]);

  React.useEffect(() => {
    if (!isVisible) return;

    function handleClickOutside(event: MouseEvent) {
      if (!rootRef.current) return;
      if (!rootRef.current.contains(event.target as Node)) {
        closeDropdown(false);
      }
    }

    function handleDocumentKeyDown(event: KeyboardEvent) {
      if (event.key === 'Escape') {
        closeDropdown(true);
      }
    }

    document.addEventListener('mousedown', handleClickOutside);
    document.addEventListener('keydown', handleDocumentKeyDown);

    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
      document.removeEventListener('keydown', handleDocumentKeyDown);
    };
  }, [isVisible, closeDropdown]);

  React.useEffect(() => {
    setHighlightedIndex(0);
  }, [query, isVisible]);

  function handleTriggerClick() {
    if (disabled) return;
    if (isOpen) {
      closeDropdown(false);
    } else {
      openDropdown();
    }
  }

  function handleTriggerKeyDown(event: React.KeyboardEvent<HTMLButtonElement>) {
    if (disabled) return;

    if (
      event.key === 'Enter' ||
      event.key === ' ' ||
      event.key === 'ArrowDown'
    ) {
      event.preventDefault();

      if (isOpen) {
        closeDropdown(false);
      } else {
        openDropdown();
      }
    }
  }

  function selectOption(nextValue: string) {
    const nextOption = options.find((option) => option.value === nextValue);
    if (nextOption?.disabled) return;

    onChange(nextValue);
    closeDropdown(true);
  }

  function handleSearchKeyDown(event: React.KeyboardEvent<HTMLInputElement>) {
    if (!filteredOptions.length) return;

    if (event.key === 'ArrowDown') {
      event.preventDefault();
      setHighlightedIndex((prev) =>
        prev < filteredOptions.length - 1 ? prev + 1 : prev,
      );
    }

    if (event.key === 'ArrowUp') {
      event.preventDefault();
      setHighlightedIndex((prev) => (prev > 0 ? prev - 1 : 0));
    }

    if (event.key === 'Enter') {
      event.preventDefault();
      const option = filteredOptions[highlightedIndex];
      if (option) {
        selectOption(option.value);
      }
    }
  }

  function handleMenuKeyDown(event: React.KeyboardEvent<HTMLDivElement>) {
    if (!filteredOptions.length) return;

    if (event.key === 'ArrowDown') {
      event.preventDefault();
      setHighlightedIndex((prev) =>
        prev < filteredOptions.length - 1 ? prev + 1 : prev,
      );
    }

    if (event.key === 'ArrowUp') {
      event.preventDefault();
      setHighlightedIndex((prev) => (prev > 0 ? prev - 1 : 0));
    }

    if (event.key === 'Enter') {
      event.preventDefault();
      const option = filteredOptions[highlightedIndex];
      if (option) {
        selectOption(option.value);
      }
    }
  }

  return (
    <div
      ref={rootRef}
      className={`scheduleDropdown scheduleDropdown--${variant} ${className} ${
        isOpen ? 'is-open' : ''
      } ${disabled ? 'is-disabled' : ''}`}
    >
      <div className="scheduleDropdown__labelSelectBroadcast">
        Selecione uma emissora
      </div>
      <button
        ref={buttonRef}
        type="button"
        className="scheduleDropdown__btn"
        aria-haspopup="listbox"
        aria-expanded={isOpen}
        aria-controls={menuId}
        disabled={disabled}
        onClick={handleTriggerClick}
        onKeyDown={handleTriggerKeyDown}
      >
        <span
          className={`scheduleDropdown__label ${
            selectedOption ? 'is-filled' : 'is-placeholder'
          }`}
        >
          {selectedOption?.label ?? placeholder}
        </span>

        <span className="scheduleDropdown__caretWrap" aria-hidden="true">
          <svg
            className="scheduleDropdown__caret"
            xmlns="http://www.w3.org/2000/svg"
            viewBox="0 0 12 12"
            fill="none"
            stroke="currentColor"
            strokeWidth="1.6"
            strokeLinecap="round"
            strokeLinejoin="round"
            width="12"
            height="12"
          >
            <path d="M3 5 L6 8 L9 5" />
          </svg>
        </span>
      </button>

      {isVisible ? (
        <div
          id={menuId}
          className={`scheduleDropdown__menu${isOpen ? ' is-open' : ''}`}
          role="listbox"
          aria-label={ariaLabel}
          tabIndex={-1}
          onKeyDown={handleMenuKeyDown}
        >
          {searchable ? (
            <div className="scheduleDropdown__searchWrap">
              <input
                ref={searchRef}
                type="text"
                className="scheduleDropdown__search"
                placeholder="Buscar"
                value={query}
                onChange={(event) => setQuery(event.target.value)}
                onKeyDown={handleSearchKeyDown}
              />
            </div>
          ) : null}

          <div className="scheduleDropdown__list">
            {filteredOptions.length ? (
              filteredOptions.map((option, index) => (
                <button
                  key={option.value}
                  type="button"
                  role="option"
                  aria-selected={option.value === value}
                  aria-disabled={option.disabled ? 'true' : 'false'}
                  disabled={option.disabled}
                  className={`scheduleDropdown__item ${
                    option.value === value ? 'is-selected' : ''
                  } ${index === highlightedIndex ? 'is-highlighted' : ''} ${
                    option.disabled ? 'is-disabled' : ''
                  }`}
                  onMouseEnter={() => {
                    if (!option.disabled) {
                      setHighlightedIndex(index);
                    }
                  }}
                  onClick={() => selectOption(option.value)}
                >
                  <span className="scheduleDropdown__itemLabel">
                    {option.label}
                  </span>

                  {option.hint ? (
                    <span className="scheduleDropdown__itemHint">
                      {option.hint}
                    </span>
                  ) : null}
                </button>
              ))
            ) : (
              <div className="scheduleDropdown__empty">
                Nenhum resultado encontrado.
              </div>
            )}
          </div>
        </div>
      ) : null}
    </div>
  );
}
